/**
 * Compliance Setup Page
 * 
 * Initial setup wizard for company legal compliance profile
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { CompanyProfileWizard } from '@/components/compliance/CompanyProfileWizard';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/toast/useToast';
import type { CompanyLegalProfile } from '@lebusiness/types';

export const Route = createFileRoute('/apps/compliance/setup')({
  component: ComplianceSetup,
});

function ComplianceSetup() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleComplete = async (profile: CompanyLegalProfile) => {
    try {
      // Get current user and company
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) throw new Error('Aucune entreprise trouvée');

      // Update company with legal profile
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          legal_form: profile.legal_form,
          activity_type: profile.activity_type,
          activity_code_nae: profile.activity_code_nae,
          tax_regime: profile.tax_regime,
          estimated_annual_revenue: profile.estimated_annual_revenue,
          employee_count: profile.employee_count,
          is_exporter: profile.is_exporter,
          is_importer: profile.is_importer,
          rc_extracted_data: profile.rc_extracted_data,
        })
        .eq('id', userData.company_id);

      if (updateError) throw updateError;

      // TODO: Generate compliance calendar
      // This will be implemented when we build the compliance engine

      toast({
        title: 'Profil sauvegardé !',
        description: 'Votre profil juridique a été configuré avec succès.',
      });

      // Navigate to compliance dashboard
      navigate({ to: '/apps/compliance' });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la sauvegarde',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    navigate({ to: '/dashboard' });
  };

  return (
    <div className="container mx-auto py-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold">Configuration de la conformité légale</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Configurez votre profil juridique pour bénéficier d'un suivi automatique de vos obligations fiscales
        </p>
      </div>

      <CompanyProfileWizard onComplete={handleComplete} onCancel={handleCancel} />
    </div>
  );
}
